import React from 'react';

import {chemicalBaseTypes, seasonTypes, systemTypes, userLevels} from './constants.jsx';

import {getIdealRange as getWardenIdealRange} from './idealRanges';

export function addCommas(num) {
  return (num + '').split('').reduce((acc, x, i, arr) => (arr.length - 1 - i) % 3 === 0 && i !== arr.length - 1 ? acc + x + ',' : acc + x);
}

export const poolOptions = {
  finish: {
    B: 'Pebbled',
    C: 'Colored Plaster',
    F: 'Fiberglass',
    P: 'Painted',
    S: 'Stainless Steel',
    U: 'Unprinted Vinyl',
    V: 'Printed Vinyl',
    W: 'White Plaster',
  },
  property: {
    C: 'Commercial',
    R: 'Residential',
  },
  location: {
    I: 'Indoor',
    O: 'Outdoor',
  },
  groundLevel: {
    A: 'Above Ground',
    I: 'In Ground',
    S: 'Splasher',
  },
  filterMedia: {
    '': '(none)',
    Sand: 'Sand',
    DE: 'DE',
    Cartridge: 'Cartridge',
    Glass: 'Glass',
    ZeoBrite: 'Zeo Brite',
  },
};

export const poolOptionsTags = {
  finish: {
    B: 'general.finish.pebbled',
    C: 'general.finish.plaster.colored',
    F: 'general.finish.fiberglass',
    P: 'general.finish.painted',
    S: 'general.finish.stainless-steel',
    U: 'general.finish.vinyl.unprinted',
    V: 'general.finish.vinyl.printed',
    W: 'general.finish.plaster.white',
  },
  property: {
    C: 'general.property.commercial',
    R: 'general.property.residential',
  },
  location: {
    I: 'general.location.indoor',
    O: 'general.location.outdoor',
  },
  groundLevel: {
    A: 'general.groundLevel.above',
    I: 'general.groundLevel.in',
    S: 'general.groundLevel.splasher',
  },
  filterMedia: {
    '': 'general.filterMedia.none',
    Sand: 'general.filterMedia.sand',
    DE: 'general.filterMedia.de',
    Cartridge: 'general.filterMedia.cartridge',
    Glass: 'general.filterMedia.glass',
    ZeoBrite: 'general.filterMedia.zeoBrite',
  },
};

export const spaOptions = {
  finish: {
    A: 'Acrylic',
    F: 'Fiberglass',
    P: 'Plaster',
    C: 'Colored Plaster',
    W: 'Wooden Tub',
    T: 'Thermal Plastic',
    O: 'Other',
  },
  property: poolOptions.property,
  location: poolOptions.location,
  groundLevel: poolOptions.groundLevel,
  filterMedia: poolOptions.filterMedia,
};

export const spaOptionsTags = {
  finish: {
    A: 'general.finish.acrylic',
    F: 'general.finish.fiberglass',
    P: 'general.finish.plaster',
    C: 'general.finish.plaster.colored',
    W: 'general.finish.tub.wooden',
    T: 'general.finish.plastic.thermal',
    O: 'general.finish.other',
  },
  property: poolOptionsTags.property,
  location: poolOptionsTags.location,
  groundLevel: poolOptionsTags.groundLevel,
  filterMedia: poolOptionsTags.filterMedia,
};

export const swimspaOptions = {
  finish: {
    A: 'Acrylic',
    F: 'Fiberglass',
    PL: 'Plaster',
    V: 'Vinyl',
  },
  property: poolOptions.property,
  location: poolOptions.location,
  groundLevel: {},
  filterMedia: poolOptions.filterMedia,
};

export const swimspaOptionsTags = {
  finish: {
    A: 'general.finish.acrylic',
    F: 'general.finish.fiberglass',
    PL: 'general.finish.plaster',
    V: 'general.finish.vinyl',
  },
  property: poolOptionsTags.property,
  location: poolOptionsTags.location,
  groundLevel: {},
  filterMedia: poolOptionsTags.filterMedia,
};

export const systemOptions = [
  null,
  poolOptions,
  spaOptions,
  swimspaOptions,
];

export const systemOptionsTags = [
  null,
  poolOptionsTags,
  spaOptionsTags,
  swimspaOptionsTags,
];

export const optimizerPlusTitle = (branch, pool, formatMessage) => {
  let translate = undefined !== formatMessage && null !== formatMessage;

  if (pool.typeID === systemTypes.spa) {
    return translate ? formatMessage({id: 'general.optimizer.plus'}) : 'Optimizer Plus';
  }
  if (branch && branch.state === 'CA') {
    return translate ? formatMessage({id: 'general.maximizer'}) : 'Maximizer';
  }
  return translate ? formatMessage({id: 'general.optimizer.plus'}) : 'Optimizer Plus';
};

export function fillStars(numStars) {
  let output = [];

  for (let i = 0; i < Math.min(numStars, 5); ++i) {
    output.push(<i className='bl bl-star-filled'
                   key={'star' + i}/>);
  }
  for (let j = Math.min(numStars, 5); j < 5; ++j) {
    output.push(<i className='bl bl-star'
                   key={'emptystar' + j}/>);
  }

  return output;
}

export const idealRanges = {
  season: {
    title: 'Analysis Type',
    displayOptions: {
      in: 'In Season',
      out: 'Out of Season',
      springopening: 'Spring Opening',
      winterclosing: 'Winter Closing',
    },
    condition: (analysis, pool) => {
      return pool.typeID !== systemTypes.spa;
    },
    separator: true,
  },
  temperature: {
    title: 'Temperature',
    required: true,
    separator: (analysis, pool) => { // Separator if spa since no season
      return pool.typeID === systemTypes.spa;
    },
  },
  saturationIndex: {
    title: 'Saturation Index',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'saturationIndex', country);
      }

      let range = [-0.5, 0.5];

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID) {
          let chemType = sanitizer.chemicalBaseTypeID;

          if (analysis.season !== 'winterclosing' &&
            (chemType === chemicalBaseTypes.chlorinePool ||
              chemType === chemicalBaseTypes.brominePool ||
              chemType === chemicalBaseTypes.mineralSpringsPool ||
              chemType === chemicalBaseTypes.saltWaterPool ||
              chemType === chemicalBaseTypes.softswimPool)) {
            range = [-0.3, 0.3];
          }
        }
      }
      return range;
    },
    required: true,
  },
  tDS: {
    title: 'TDS',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'TDS', country);
      }

      if (analysis && analysis.id && pool && pool.id && pool.chemicalBaseTypeID) {
        if (pool.chemicalBaseTypeID === chemicalBaseTypes.saltWaterPool ||
          pool.chemicalBaseTypeID === chemicalBaseTypes.saltWaterSpa) {
          return [];
        }
      }
      return [-Infinity, 2500];
    },
    required: true,
  },
  cYA: {
    title: 'CYA',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'cYA', country);
      }

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        if (pool.chemicalBaseTypeID === chemicalBaseTypes.chlorineSpa ||
          pool.chemicalBaseTypeID === chemicalBaseTypes.bromineSpa ||
          pool.chemicalBaseTypeID === chemicalBaseTypes.saltWaterSpa ||
          pool.chemicalBaseTypeID === chemicalBaseTypes.softswimPool ||
          pool.chemicalBaseTypeID === chemicalBaseTypes.softSoakSpa) {
          return [];
        }
        if (pool && (pool.chemicalBaseTypeID === chemicalBaseTypes.mineralSpringsPool ||
            pool.chemicalBaseTypeID === chemicalBaseTypes.saltWaterPool)) {
          return pool.property === 'C' ? [60, 100] : [60, 200];
        }
        if (pool && pool.chemicalBaseTypeID === chemicalBaseTypes.brominePool) {
          return [0, 0];
        }
        return pool.property === 'C' ? [30, 100] : [30, 200];
      }
      return [];
    },
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        if (pool && pool.chemicalBaseTypeID) {
          if (pool.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool &&
            pool.chemicalBaseTypeID !== chemicalBaseTypes.softSoakSpa) {
            return true;
          }
        }
      }
      return false;
    },
    required: true,
  },
  totalChlorine: {
    title: 'Total Chlorine',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'totalChlorine', country);
      }

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);
        let chemType = sanitizer.chemicalBaseTypeID;

        if (chemType === chemicalBaseTypes.bromineSpa || chemType === chemicalBaseTypes.chlorineSpa || chemType === chemicalBaseTypes.saltWaterSpa) {
          return [3, 5];
        }
        if (chemType === chemicalBaseTypes.brominePool && sanitizer.code === '242') {
          // Smart SilkGuard Sticks
          return [1.5, 3];
        }
      }
      return [1, 3];
    },
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softSoakSpa && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.brominePool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.bromineSpa) {
          return true;
        }
      }
      return false;
    },
  },
  freeChlorine: {
    title: 'Free Chlorine',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'freeChlorine', country);
      }

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);
        let chemType = sanitizer.chemicalBaseTypeID;

        if (sanitizer && sanitizer.chemicalBaseTypeID) {
          if (chemType === chemicalBaseTypes.bromineSpa || chemType === chemicalBaseTypes.chlorineSpa || chemType === chemicalBaseTypes.saltWaterSpa) {
            return [3, 5];
          }
          if (chemType === chemicalBaseTypes.brominePool && sanitizer.code === '242') {
            // Smart SilkGuard Sticks
            return [1.5, 3];
          }
        }
      }
      return [1, 3];
    },
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softSoakSpa && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.brominePool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.bromineSpa) {
          return true;
        }
      }
      return false;
    },
  },
  pH: {
    title: 'pH',
    required: true,
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'pH', country);
      }

      let range = [7.4, 7.6];

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);
        let chemType = sanitizer.chemicalBaseTypeID;

        if (sanitizer && sanitizer.chemicalBaseTypeID) {
          if (chemType === chemicalBaseTypes.softswimPool || chemType === chemicalBaseTypes.softSoakSpa) {
            range = [7.4, 7.8];
          }
        }
      }
      return range;
    },
  },
  alkalinity: {
    title: 'Total Alkalinity',
    required: true,
  },
  adjustedTA: {
    title: 'Adj. Total Alkalinity',
    required: true,
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'adjustedTA', country);
      }

      let range = [];

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);
        let chemType = sanitizer.chemicalBaseTypeID;

        if (sanitizer && sanitizer.chemicalBaseTypeID) {
          if ([
            chemicalBaseTypes.brominePool,
            chemicalBaseTypes.chlorinePool,
          ].indexOf(chemType) !== -1) {
            return analysis.season !== 'winterclosing' ? [125, 150] : [150, 175];
          }
          if ([
            chemicalBaseTypes.mineralSpringsPool,
            chemicalBaseTypes.saltWaterPool,
          ].indexOf(chemType) !== -1) {
            return analysis.season !== 'winterclosing' ? [80, 125] : [125, 175];
          }
          if ([
            chemicalBaseTypes.softswimPool,
            chemicalBaseTypes.chlorineSpa,
            chemicalBaseTypes.bromineSpa,
          ].indexOf(chemType) !== -1) {
            return [125, 150];
          }
          if (chemType === chemicalBaseTypes.saltWaterSpa) {
            return [80, 115];
          }
          if (chemType === chemicalBaseTypes.softSoakSpa) {
            return [80, 120];
          }
        }
      }
      return range;
    },
  },
  hardness: {
    title: 'Total Hardness',
    required: true,
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'hardness', country);
      }

      let range = [];

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID) {
          let chemType = sanitizer.chemicalBaseTypeID;

          if ((chemType === chemicalBaseTypes.brominePool || chemType === chemicalBaseTypes.chlorinePool) &&
            (pool.finish === 'C' || pool.finish === 'W')) {
            range = [200, 275];
          } else if (chemType === chemicalBaseTypes.brominePool || chemType === chemicalBaseTypes.chlorinePool) {
            range = [175, 225];
          } else if (chemType === chemicalBaseTypes.mineralSpringsPool || chemType === chemicalBaseTypes.saltWaterPool) {
            range = [200, 400];
          } else if (chemType === chemicalBaseTypes.softswimPool) {
            range = [200, 275];
          } else if (chemType === chemicalBaseTypes.chlorineSpa || chemType === chemicalBaseTypes.bromineSpa ||
            chemType === chemicalBaseTypes.saltWaterSpa || chemType === chemicalBaseTypes.softSoakSpa) {
            if (pool.usingVanishingAct) {
              range = [25, 75];
            } else if (chemType === chemicalBaseTypes.softSoakSpa) {
              range = [200, 300];
            } else {
              range = [100, 200];
            }
          }
        }
      }
      return range;
    },
  },
  salt: {
    title: 'Salt',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'salt', country);
      }

      let range = [];

      if (pool && pool.id && pool.idealSaltPPM) {
        range = [pool.idealSaltPPM, pool.idealSaltPPM];
      }
      return range;
    },
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.name && (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.saltWaterPool ||
            sanitizer.name === 'Saltwater Pool' || sanitizer.chemicalBaseTypeID === chemicalBaseTypes.saltWaterSpa || sanitizer.name === 'Saltwater Spa')) {
          return true;
        }
      }
      return false;
    },
  },
  minerals: {
    title: 'Minerals',
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'minerals', country);
      }

      let range = [];

      if (pool && pool.id && pool.idealSaltPPM) {
        range = [pool.idealSaltPPM, pool.idealSaltPPM];
      }
      return range;
    },
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.mineralSpringsPool) {
          return true;
        }
      }
      return false;
    },
  },
  optimizerPlusBorate: {
    title: optimizerPlusTitle,
    min: 30,
    max: 35,
    required: true,
    condition: (analysis, pool) => {
      if (pool && pool.usingOptimizerPlus) {
        return true;
      }
      return false;
    },
  },
  copper: {
    title: 'Copper',
    required: true,
    ideal: 0,
    min: 0,
    max: 0,
  },
  copperFromProduct: {
    title: 'Copper from Products',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
  },
  iron: {
    title: 'Iron',
    required: true,
    min: 0,
    max: 0,
  },
  totalBromine: {
    title: 'Total Bromine',
    getRange: (useWarden, country, analysis, pool, products) => {
      let range = [];

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);
        let chemType = sanitizer.chemicalBaseTypeID;

        if (chemType === chemicalBaseTypes.brominePool ||
          chemType === chemicalBaseTypes.chlorinePool) {
          if (pool.property === 'C') {
            range = [4, 6];
          } else if (pool.property === 'R') {
            range = [2, 4];
          }
        }
        if (chemType === chemicalBaseTypes.bromineSpa ||
          chemType === chemicalBaseTypes.chlorineSpa ||
          chemType === chemicalBaseTypes.saltWaterSpa) {
          if (sanitizer.name.toLowerCase().indexOf('concentrate') !== -1) {
            range = [3, 5];
          } else if (sanitizer.name.toLowerCase().indexOf('tab') !== -1 && pool.property === 'C') {
            range = [4, 6];
          } else if (sanitizer.name.toLowerCase().indexOf('tab') !== -1 && pool.property === 'R') {
            range = [2, 4];
          }
        }

      }
      return range;
    },
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.brominePool ||
            sanitizer.chemicalBaseTypeID === chemicalBaseTypes.bromineSpa)) {
          return true;
        }
      }
      return false;
    },
  },
  sSWB: {
    title: 'SoftSwim B',
    min: 30,
    max: 50,
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
  },
  sSWC: {
    title: 'SoftSwim C',
    min: 40,
    max: 60,
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
  },
  sSKSanitizerResidual: {
    title: 'SSK Sanitizer',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softSoakSpa) {
          return true;
        }
      }
      return false;
    },
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'sSKSanitizerResidual', country);
      }

      return [30, 50];
    },
  },
  sSKShockResidual: {
    title: 'SSK Shock',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softSoakSpa) {
          return true;
        }
      }
      return false;
    },
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'sSKShockResidual', country);
      }

      return [5, 12];
    },
  },
  clarifierDemand: {
    title: 'Clarifier Demand',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
  },
  freshFill: {
    title: 'Fresh Fill',
    required: true,
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        if (analysis && analysis.season && analysis.season !== seasonTypes.closing && analysis.season !== seasonTypes.opening) {
          return true;
        }
      }
      return false;
    },
  },
  cleanAndDrain: {
    title: 'Clean And Drain',
    condition: (analysis, pool, products) => {
      let sanitizer = products.find(x => x.id === pool.sanitizer);

      if (sanitizer && sanitizer.chemicalBaseTypeID &&
        (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.chlorineSpa || sanitizer.chemicalBaseTypeID === chemicalBaseTypes.bromineSpa) &&
        pool.softSoakTRIO) {
        return true;
      }
      return false;
    },
    required: true,
  },
  manganese: {
    title: 'Manganese',
    ideal: 'No',
  },
  quat: {
    title: 'Quat',
    condition: (analysis, pool, products) => {
      let visible = true;

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && pool.typeID) {
          visible = sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool && pool.typeID !== systemTypes.spa;
        }
      }
      return visible;
    },
    getRange: (useWarden, country, analysis, pool, products) => {

      if(useWarden) {
        return getWardenIdealRange(analysis, pool, products, 'quat', country);
      }

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let algicide = products.find(x => x.id === pool.prevAlgicide);

        if (algicide) {
          if (algicide.name === 'Back Up') {
            return [4, 4];
          }
          if (algicide.name === 'Back Up 2') {
            return [2, 2];
          }
          if (algicide.name === 'Banish') {
            return []; // N/A
          }
          if (algicide.name === 'Algae All 60') {
            return [4, 6];
          }
          if (algicide.name === 'Algicide 28 - 40') {
            return [2, 2];
          }
          if (algicide.name === 'Algicide 28-10') {
            return [2, 2];
          }
          if (algicide.name === 'ProGuard 60% Algicide') {
            return [4, 6];
          }
        }
      }
      return [4, 4];
    },
    required: true,
  },
  waterClarity: {
    title: 'Water Clarity',
    ideal: 'Clear',
    required: true,
    separator: true,
  },
  algae: {
    title: 'Algae',
    ideal: 'No',
    required: true,
  },
  surfaceProblems: {
    title: 'Surface Problems',
    ideal: 'No',
    required: true,
  },
  cDSample: {
    title: 'CD Sample (CD Standard)',
  },
  filterCleanedDate: {
    separator: true,
    title: 'Filter Cleaned',
    required: true,
  },
  lastCleanedDrained: {
    separator: true,
    title: 'Drained',
    condition: (analysis, pool) => analysis && pool && pool.typeID === systemTypes.spa,
    required: true,
  },
  waterTester: {
    separator: true,
    required: true,
    title: 'Tester',
  },
  branchName: {
    separator: true,
    required: false,
    condition: (analysis, pool, products, isHistory) => analysis && analysis.branchID && analysis.branchID > 0,
    title: 'Location',
  },
};

export const idealRangesFormatted = (formatMessage) => {
  if (undefined === formatMessage || null === formatMessage) {
    return idealRanges;
  }

  let list = idealRanges;

  list['season'].title = formatMessage({id: 'general.analysis.type'});
  list['temperature'].title = formatMessage({id: 'general.temperature'});
  list['saturationIndex'].title = formatMessage({id: 'general.saturation.index'});
  list['tDS'].title = formatMessage({id: 'general.tds'});
  list['cYA'].title = formatMessage({id: 'analysis.cya'});
  list['totalChlorine'].title = formatMessage({id: 'general.total.chlorine'});
  list['freeChlorine'].title = formatMessage({id: 'general.free.chlorine'});
  list['pH'].title = formatMessage({id: 'general.ph'});
  list['alkalinity'].title = formatMessage({id: 'general.total.alkalinity'});
  list['adjustedTA'].title = formatMessage({id: 'general.adj.total.alkalinity'});
  list['hardness'].title = formatMessage({id: 'general.total.hardness'});
  list['salt'].title = formatMessage({id: 'general.salt'});
  list['minerals'].title = formatMessage({id: 'general.minerals'});
  list['copper'].title = formatMessage({id: 'general.copper'});
  list['copperFromProduct'].title = formatMessage({id: 'general.copper.products'});
  list['iron'].title = formatMessage({id: 'general.iron'});
  list['totalBromine'].title = formatMessage({id: 'general.total.bromine'});
  list['sSWB'].title = formatMessage({id: 'general.softswim'}) + ' B';
  list['sSWC'].title = formatMessage({id: 'general.softswim'}) + ' C';
  list['sSKSanitizerResidual'].title = formatMessage({id: 'general.ssk.sanitizer'});
  list['sSKShockResidual'].title = formatMessage({id: 'general.ssk.shock'});
  list['clarifierDemand'].title = formatMessage({id: 'scan.clarifier.demand'});
  list['freshFill'].title = formatMessage({id: 'analysis.fresh.fill'});
  list['cleanAndDrain'].title = formatMessage({id: 'analysis.clean.drain'});
  list['manganese'].title = formatMessage({id: 'general.manganese'});
  list['quat'].title = formatMessage({id: 'general.quat'});
  list['waterClarity'].title = formatMessage({id: 'general.water.clarity'});
  list['algae'].title = formatMessage({id: 'general.algae'});
  list['surfaceProblems'].title = formatMessage({id: 'general.surface.problems'});
  list['cDSample'].title = formatMessage({id: 'general.cd.sample.standard'});
  list['filterCleanedDate'].title = formatMessage({id: 'general.filter.cleaned'});
  list['lastCleanedDrained'].title = formatMessage({id: 'general.drained'});
  list['waterTester'].title = formatMessage({id: 'general.tester'});
  list['branchName'].title = formatMessage({id: 'general.location'});

  return list;
};

export function getIdealRange(property, analysis, pool, products, country, useWarden) {
  let min, max;

  if (idealRanges && idealRanges[property] && idealRanges[property].hasOwnProperty('min') && idealRanges[property].hasOwnProperty('max')) {
    min = idealRanges[property].min;
    max = idealRanges[property].max;
  } else if (idealRanges && idealRanges[property] && idealRanges[property].getRange) {
    [min, max] = idealRanges[property].getRange(useWarden, country, analysis, pool, products);
  } else {
    return '';
  }
  let output;

  if (min === -Infinity && max !== Infinity) {
    output = `< ${max}`;
  } else if (min === max) {
    output = min || min === 0 ? min.toString() : '';
  } else {
    output = `${min} - ${max}`;
  }
  return output;
}

export function getMinMax(property, analysis, pool, products, country, useWarden) {
  let min, max;

  if (idealRanges && idealRanges[property] && idealRanges[property].min && idealRanges[property].max) {
    min = idealRanges[property].min;
    max = idealRanges[property].max;
  } else if (idealRanges && idealRanges[property] && idealRanges[property].getRange) {
    [min, max] = idealRanges[property].getRange(useWarden, country, analysis, pool, products);
  }

  if (property === 'totalChlorine') {
    max = Math.min(max, analysis.freeChlorine + 0.399);
  }
  return [min, max];
}

export function getContactName(contact, defaultContactName) {
  if (contact.nameFirst || contact.nameLast) {
    defaultContactName = (contact.nameFirst || '') + ' ' + (contact.nameLast || '');
  } else if (contact.organization) {
    defaultContactName = contact.organization;
  }
  return defaultContactName.trim();
}

export function gallonsToLitres(gal) {
  return gal * 3.78541; // Convert to L (constant from google)
}


export const userRoles = new Map([
  [1000, 'Test Station'],
  [2000, 'Store Admin'],
  [3000, 'Dealer Admin'],
]);

export function canDeleteCustomers(userPrivileges, dealer) {
  if (userPrivileges.level === userLevels.testStation) {
    return dealer.testerDeleteCustomers;
  } else if (userPrivileges.level === userLevels.manager) {
    return dealer.managerDeleteCustomers;
  } else if (userPrivileges.level === userLevels.dealerAdmin) {
    return true;
  }
  return false;
}

export function canDeleteSystems(userPrivileges, dealer) {
  if (userPrivileges.level === userLevels.testStation) {
    return dealer.testerDeleteSystems;
  } else if (userPrivileges.level === userLevels.manager) {
    return dealer.managerDeleteSystems;
  } else if (userPrivileges.level === userLevels.dealerAdmin) {
    return true;
  }
  return false;
}
